<script setup lang="ts">
  import { IconImage } from '@/shared/ui';
  import { useCustomToast } from '@/shared/lib';
  import PrinterIcon from '@/shared/assets/icons/PrinterIcon.svg';
  import {
    buildModalPrintDocument,
    buildPrintBodyHtml,
    buildPrintHeaderHtml,
    collectPrintStyles,
    ensurePrintSvgViewBox,
    syncFormFieldValues,
    waitForPrintAssets,
  } from '@/shared/lib/print';

  const props = withDefaults(
    defineProps<{
      title?: string;
      printStyles?: string;
      /** Label of the pill shown to the left of the button, hidden when empty. */
      badge?: string;
      /** Spacing of the row, so a consumer can match the padding of its own design. */
      containerClass?: string;
      /** Shape of the button, so a consumer can match the size of its own design. */
      buttonClass?: string;
      resolveLogoSrc?: () => Promise<string>;
    }>(),
    {
      title: 'Documento',
      printStyles: '',
      badge: '',
      containerClass: 'mb-6 px-6',
      buttonClass: 'px-3.25 py-3 rounded',
      resolveLogoSrc: undefined,
    }
  );

  const { showErrorToast } = useCustomToast();

  const BLOCKED_POPUP_ERROR_MESSAGE =
    'Não foi possível abrir a janela de impressão. Verifique se o navegador bloqueou a janela e tente novamente.';
  const PRINT_GENERATION_ERROR_MESSAGE = 'Não foi possível gerar a impressão. Tente novamente.';

  const handlePrint = async () => {
    const printContent = document.getElementById('modal-content');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showErrorToast({ title: BLOCKED_POPUP_ERROR_MESSAGE, type: 'error' });
      return;
    }

    try {
      syncFormFieldValues(printContent);

      const [headerElement, ...bodyElements] = Array.from(printContent.children);
      const headerHtml = buildPrintHeaderHtml(headerElement, await props.resolveLogoSrc?.());
      const bodyHtml = buildPrintBodyHtml(bodyElements);

      const extraStyles = props.printStyles ? `<style>${props.printStyles}</style>` : '';

      printWindow.document.write(
        buildModalPrintDocument({
          title: props.title,
          headerHtml,
          bodyHtml,
          styles: `${collectPrintStyles()}\n${extraStyles}`,
          repeatHeaderOnEveryPage: true,
        })
      );
      printWindow.document.close();
      ensurePrintSvgViewBox(printWindow);
      await waitForPrintAssets(printWindow);
      printWindow.print();
    } catch {
      printWindow.close();
      showErrorToast({ title: PRINT_GENERATION_ERROR_MESSAGE, type: 'error' });
    }
  };
</script>

<template>
  <div
    class="flex justify-end items-center gap-2.5 border-b border-gray-02 pb-6 print:hidden"
    :class="containerClass"
  >
    <span
      v-if="badge"
      class="rounded-full bg-gray-02 px-2 py-1 text-sm/5 font-semibold text-gray-07"
      data-testid="print-button-badge"
    >
      {{ badge }}
    </span>
    <!--
      The control hides itself in print instead of relying on the row: with a
      badge the row has two children, and `buildPrintBodyHtml` flattens any div
      with more than one child into separate rows, which would drop the
      `print:hidden` of the row and print the button along with the content.
    -->
    <button
      class="bg-[var(--theme-secondary)] cursor-pointer text-white flex items-center gap-2 transition-colors print:hidden"
      :class="buttonClass"
      @click="handlePrint"
    >
      <IconImage
        name="PrinterIcon"
        :src="PrinterIcon"
        alt="Imprimir"
      />
    </button>
  </div>
</template>
